import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Home, ScanBarcode, Search, Loader2, Save, Printer } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import BarcodeScanner from "@/components/BarcodeScanner";
import BottomNav from "@/components/BottomNav";
import Barcode from "@/components/Barcode";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { printPriceTags4x4 } from "@/lib/printTags";

interface DealRow {
  id: string;
  name: string;
  batch_code: string;
}
interface DealItemRow {
  id: string;
  name: string;
  platform: string | null;
  upc: string | null;
  grade_price: string | null;
}

const Upc = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [upc, setUpc] = useState("");
  const [scanned, setScanned] = useState<string | null>(null);
  const [isScanning, setIsScanning] = useState(false);
  const [lookup, setLookup] = useState<{ name?: string; brand?: string; category?: string; description?: string; image?: string } | null>(null);
  const [looking, setLooking] = useState(false);

  const [deals, setDeals] = useState<DealRow[]>([]);
  const [dealId, setDealId] = useState<string>("");
  const [items, setItems] = useState<DealItemRow[]>([]);
  const [itemId, setItemId] = useState<string>("");
  const [loadingItems, setLoadingItems] = useState(false);
  const [saving, setSaving] = useState(false);

  const [options, setOptions] = useState<Array<{ name: string; platform: string; image: string | null; url: string }>>([]);

  const runLookup = async (code: string) => {
    setLooking(true);
    setLookup(null);
    setOptions([]);
    try {
      const { data, error } = await supabase.functions.invoke("upc-lookup", { body: { upc: code } });
      if (error) throw error;
      if (data?.found) {
        setLookup(data);
        return;
      }
      // No exact match — search PriceCharting for candidate options
      const { data: searchData, error: searchErr } = await supabase.functions.invoke(
        "pricecharting-search",
        { body: { q: code } },
      );
      if (searchErr) throw searchErr;
      const results = searchData?.results || [];
      if (results.length > 0) {
        setOptions(results);
        toast({ title: "No exact match", description: `Showing ${results.length} possible matches.` });
      } else {
        toast({ title: "No match", description: `No entries found for ${code}.` });
      }
    } catch (e: any) {
      toast({ title: "Lookup failed", description: e.message, variant: "destructive" });
    } finally {
      setLooking(false);
    }
  };

  const pickOption = (opt: { name: string; platform: string; image: string | null }) => {
    setLookup({ name: opt.name, brand: opt.platform, image: opt.image || undefined });
    setOptions([]);
  };

  useEffect(() => {
    (async () => {
      const { data, error } = await supabase
        .from("deals")
        .select("id, name, batch_code")
        .order("created_at", { ascending: false })
        .limit(50);
      if (!error && data) setDeals(data);
    })();
  }, []);

  useEffect(() => {
    if (!dealId) {
      setItems([]);
      setItemId("");
      return;
    }
    (async () => {
      setLoadingItems(true);
      const { data, error } = await supabase
        .from("deal_items")
        .select("id, name, platform, upc, grade_price")
        .eq("deal_id", dealId)
        .order("created_at", { ascending: true });
      if (!error && data) setItems(data);
      setLoadingItems(false);
    })();
  }, [dealId]);

  const handleScan = (code: string) => {
    setUpc(code);
    setScanned(code);
    runLookup(code);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const v = upc.trim();
    if (!v) return;
    setScanned(v);
    runLookup(v);
  };

  const saveUpc = async () => {
    if (!scanned || !itemId) {
      toast({ title: "Missing info", description: "Scan a UPC and pick a deal item.", variant: "destructive" });
      return;
    }
    setSaving(true);
    const { error } = await supabase
      .from("deal_items")
      .update({ upc: scanned })
      .eq("id", itemId);
    setSaving(false);
    if (error) {
      toast({ title: "Error", description: error.message, variant: "destructive" });
      return;
    }
    toast({ title: "Saved", description: `UPC ${scanned} attached to item.` });
    setItems((prev) => prev.map((i) => (i.id === itemId ? { ...i, upc: scanned } : i)));
    setScanned(null);
    setUpc("");
    setItemId("");
  };

  return (
    <div className="min-h-screen bg-background pb-24 relative">
      <div className="fixed inset-0 pointer-events-none scanline opacity-30 z-50" />

      <div className="relative z-10 max-w-md mx-auto px-4 py-6 space-y-6">
        <header className="relative text-center space-y-2">
          <button
            onClick={() => navigate("/")}
            className="absolute top-0 left-0 flex items-center gap-1 text-xs font-mono text-muted-foreground hover:text-primary transition-colors"
          >
            <Home className="w-4 h-4" />
            HOME
          </button>
          <ScanBarcode className="w-10 h-10 mx-auto" style={{ color: "hsl(25 100% 55%)" }} />
          <h1 className="text-2xl font-mono font-bold tracking-widest text-primary">UPC</h1>
          <p className="text-xs font-mono text-muted-foreground tracking-wider">
            SCAN A UPC BARCODE
          </p>
        </header>

        <BarcodeScanner onScan={handleScan} isScanning={isScanning} setIsScanning={setIsScanning} />

        <form onSubmit={handleSubmit} className="flex gap-2">
          <Input
            value={upc}
            onChange={(e) => setUpc(e.target.value)}
            placeholder="Enter UPC manually..."
            className="font-mono bg-card border-border focus:border-primary"
          />
          <Button type="submit" disabled={!upc.trim()} className="bg-primary text-primary-foreground font-mono font-bold shrink-0">
            <Search className="w-4 h-4" />
          </Button>
        </form>

        {scanned && (
          <div className="p-4 rounded-lg border border-primary/40 bg-card text-center space-y-1">
            <p className="text-[10px] font-mono text-muted-foreground tracking-widest">SCANNED UPC</p>
            <p className="font-mono text-xl text-primary tracking-widest break-all">{scanned}</p>
            {looking && (
              <p className="text-[10px] font-mono text-muted-foreground flex items-center justify-center gap-1 pt-1">
                <Loader2 className="w-3 h-3 animate-spin" /> LOOKING UP...
              </p>
            )}
          </div>
        )}

        {lookup && (
          <div className="p-4 rounded-lg border border-border bg-card space-y-2">
            <p className="text-[10px] font-mono text-muted-foreground tracking-widest">GO-UPC RESULT</p>
            <div className="flex gap-3">
              {lookup.image && (
                <img src={lookup.image} alt={lookup.name} className="w-20 h-20 object-contain bg-white rounded border border-border" />
              )}
              <div className="flex-1 min-w-0 space-y-1">
                <p className="font-mono text-sm text-primary font-bold leading-tight">{lookup.name}</p>
                {lookup.brand && <p className="font-mono text-[10px] text-muted-foreground">BRAND: {lookup.brand}</p>}
                {lookup.category && <p className="font-mono text-[10px] text-muted-foreground">CATEGORY: {lookup.category}</p>}
              </div>
            </div>
            {lookup.description && <p className="font-mono text-[11px] text-foreground/80">{lookup.description}</p>}
          </div>
        )}

        {options.length > 0 && (
          <div className="p-4 rounded-lg border border-border bg-card space-y-2">
            <p className="text-[10px] font-mono text-muted-foreground tracking-widest">
              POSSIBLE MATCHES — PICK ONE
            </p>
            <div className="space-y-2 max-h-72 overflow-y-auto">
              {options.map((o, i) => (
                <button
                  key={i}
                  type="button"
                  onClick={() => pickOption(o)}
                  className="w-full text-left flex gap-3 p-2 rounded border border-border hover:border-primary hover:bg-primary/5 transition-colors"
                >
                  {o.image ? (
                    <img
                      src={o.image}
                      alt={o.name}
                      className="w-12 h-14 object-contain bg-white rounded border border-border shrink-0"
                    />
                  ) : (
                    <div className="w-12 h-14 bg-background rounded border border-border shrink-0" />
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="font-mono text-xs text-primary font-bold leading-tight truncate">
                      {o.name}
                    </p>
                    {o.platform && (
                      <p className="font-mono text-[10px] text-muted-foreground mt-0.5">
                        {o.platform}
                      </p>
                    )}
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}

        <div className="p-4 rounded-lg border border-border bg-card space-y-3">
          <p className="text-[10px] font-mono text-muted-foreground tracking-widest">ATTACH UPC TO DEAL ITEM</p>

          <Select value={dealId} onValueChange={setDealId}>
            <SelectTrigger className="font-mono bg-background">
              <SelectValue placeholder="Select a deal..." />
            </SelectTrigger>
            <SelectContent>
              {deals.map((d) => (
                <SelectItem key={d.id} value={d.id} className="font-mono text-xs">
                  {d.name} — {d.batch_code}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select value={itemId} onValueChange={setItemId} disabled={!dealId || loadingItems}>
            <SelectTrigger className="font-mono bg-background">
              <SelectValue placeholder={loadingItems ? "Loading..." : "Select an item..."} />
            </SelectTrigger>
            <SelectContent>
              {items.map((i) => (
                <SelectItem key={i.id} value={i.id} className="font-mono text-xs">
                  {i.name}{i.platform ? ` (${i.platform})` : ""}{i.upc ? ` • UPC ${i.upc}` : ""}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          {(() => {
            const selected = items.find((i) => i.id === itemId);
            if (!selected) return null;
            const upcForTag = scanned || selected.upc || "";
            const priceNum = Number(selected.grade_price || 0);
            return (
              <div className="space-y-2">
                <p className="text-[10px] font-mono text-muted-foreground tracking-widest">LABEL PREVIEW</p>
                <div
                  className="mx-auto bg-white text-black border border-border"
                  style={{ width: "2in", height: "0.75in", padding: "0.05in 0.07in", fontFamily: "'JetBrains Mono', ui-monospace, monospace" }}
                >
                  <div className="flex flex-row-reverse items-center gap-[0.06in] h-full w-full">
                    <div className="shrink-0 flex items-center justify-center" style={{ width: "0.7in" }}>
                      <div style={{ fontSize: 18, fontWeight: 800, lineHeight: 1 }}>${priceNum.toFixed(2)}</div>
                    </div>
                    <div className="flex-1 min-w-0 h-full flex flex-col justify-between" style={{ padding: "0.02in 0" }}>
                      <div className="text-right w-full" style={{ fontSize: 7, fontWeight: 800, textTransform: "uppercase", letterSpacing: "0.04em", lineHeight: 1 }}>
                        Lynx to the Past
                      </div>
                      <div className="text-right w-full truncate" style={{ fontSize: 8, fontWeight: 700, lineHeight: 1.05 }}>
                        {lookup?.name || selected.name}
                      </div>
                      {upcForTag && (
                        <div className="w-full bg-white" style={{ height: "0.32in" }}>
                          <Barcode value={upcForTag} height={28} width={1} displayValue={false} className="w-full h-full" />
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            );
          })()}

          <Button
            onClick={saveUpc}
            disabled={!scanned || !itemId || saving}
            className="w-full bg-primary text-primary-foreground font-mono font-bold tracking-wider"
          >
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
            SAVE UPC TO ITEM
          </Button>

          <Button
            onClick={() => {
              const selected = items.find((i) => i.id === itemId);
              if (!selected) return;
              const upcForTag = scanned || selected.upc || "";
              if (!upcForTag) return;
              printPriceTags4x4([{ name: lookup?.name || selected.name, price: Number(selected.grade_price || 0), upc: upcForTag, image: lookup?.image }]);
            }}
            disabled={!itemId || !(scanned || items.find((i) => i.id === itemId)?.upc)}
            variant="outline"
            className="w-full font-mono tracking-wider border-primary/40 text-primary"
          >
            <Printer className="w-4 h-4 mr-2" />
            PRINT LABEL
          </Button>
        </div>
      </div>

      <BottomNav />
    </div>
  );
};

export default Upc;
